/**
 * Pure auth helpers shared by AuthContext.authFetch, the challenge modal, and
 * the invite (sign-up) flow. No React and no side effects, so they're unit
 * testable with `node --test`.
 */

/**
 * A Clerk `getToken()` result is only usable if it's a non-empty string.
 * getToken() resolves to null/undefined when the session isn't ready — notably
 * flaky on Chrome with strict cookie / privacy settings. Interpolating that
 * into a header produces the literal "Bearer null" / "Bearer undefined", which
 * the API rejects with a confusing generic 401 instead of a clear
 * "session expired" signal. Guard against both the nullish values and their
 * stringified forms.
 */
export function hasUsableToken(token) {
  return (
    typeof token === 'string' &&
    token.length > 0 &&
    token !== 'null' &&
    token !== 'undefined'
  );
}

export const SESSION_EXPIRED = {
  code: 'session_expired',
  message: 'Your session expired. Please refresh the page and sign in again.'
};

/**
 * Synthetic 401 returned by authFetch when no valid token can be obtained, so
 * every authenticated call fails the same clean, actionable way instead of
 * emitting "Bearer null" to the server.
 */
export function sessionExpiredResponse() {
  return new Response(
    JSON.stringify({ error: SESSION_EXPIRED.message, code: SESSION_EXPIRED.code }),
    { status: 401, headers: { 'Content-Type': 'application/json' } }
  );
}

/**
 * Turn an API response status + parsed body into a message safe to show a user.
 */
export function friendlyRequestError(status, body) {
  if (status === 401) return SESSION_EXPIRED.message;
  if (body && typeof body.error === 'string' && body.error) return body.error;
  return 'Something went wrong. Please try again.';
}

/**
 * Detect Clerk sign-up failures caused by the Cloudflare Turnstile / Smart
 * CAPTCHA widget being blocked. Chrome ad-block and strict privacy extensions
 * silently kill the Turnstile script, so `signUp.create()` fails with a captcha
 * error — the #1 "reps can't sign up / log in on Chrome" cause. Matches only
 * captcha/turnstile signals so it never swallows email-verification or
 * identifier-exists errors.
 */
export function isCaptchaBlockedError(error) {
  if (!error) return false;
  const code = (error?.errors?.[0]?.code || error?.code || '').toLowerCase();
  const msg = (
    error?.errors?.[0]?.message ||
    error?.long_message ||
    error?.message ||
    ''
  ).toLowerCase();
  return code.includes('captcha') || msg.includes('captcha') || msg.includes('turnstile');
}

export const CAPTCHA_BLOCKED_MESSAGE =
  'We could not complete the browser security check. This is almost always an ' +
  'ad-blocker or a strict privacy setting in Chrome blocking it. Please turn off ' +
  'your ad-blocker for this site (or open an incognito window / try another ' +
  'browser), then try again.';
